import clsx from 'clsx';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export interface ToastInput {
  title: string;
  body?: string;
  tone?: 'info' | 'success' | 'error' | 'alert';
}
interface ToastItem extends ToastInput {
  id: number;
}
interface ToastApi {
  show: (t: ToastInput) => void;
  error: (e: unknown, fallback?: string) => void;
  success: (title: string) => void;
}

const Ctx = createContext<ToastApi>({ show: () => undefined, error: () => undefined, success: () => undefined });
export const useToast = () => useContext(Ctx);

const TONE: Record<NonNullable<ToastInput['tone']>, string> = {
  info: 'bg-slate-900 text-white',
  success: 'bg-emerald-700 text-white',
  error: 'bg-rose-700 text-white',
  alert: 'bg-amber-500 text-slate-900',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const show = useCallback((t: ToastInput) => {
    const id = ++seq.current;
    setItems((cur) => [...cur.slice(-3), { ...t, id }]);
    setTimeout(() => setItems((cur) => cur.filter((x) => x.id !== id)), t.tone === 'alert' ? 9000 : 4500);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title) => show({ title, tone: 'success' }),
      error: (e, fallback = 'Something went wrong') => show({ title: e instanceof Error && e.message ? e.message : fallback, tone: 'error' }),
    }),
    [show],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-3"
        style={{ top: 'calc(var(--safe-top) + 0.75rem)' }}
      >
        {items.map((t) => (
          <div key={t.id} role="status" className={clsx('pointer-events-auto w-full max-w-md rounded-2xl px-4 py-3 shadow-lg', TONE[t.tone ?? 'info'])}>
            <div className="text-sm font-semibold">{t.title}</div>
            {t.body && <div className="mt-0.5 text-sm opacity-90">{t.body}</div>}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
